import { actionNestedFieldKey } from "./actionCatalog";

export const BOOKING_TRIGGER_TYPES = new Set([
  "NEW_BOOKING",
  "BOOKING_CANCELED",
]);

// Org-option config fields (opaque resource ids). allowance_unit_id is not required by
// api-v2: it is branch-required on AI-created Limited allowances only
// (BRANCH_REQUIRED_FIELDS). Do not add it to api-v2.
export const ORG_OPTION_REQUIRED_FIELDS = new Set([
  "mail_provider_credentials_id",
  "location_id",
  "access_template_id",
  "type_label_id",
  "property_id",
  "target_group_conversation_id",
  "allowance_unit_id",
]);

/** Mirrors api-v2 WorkflowAction::getBaselineRequiredConfigFields(). */
export const BASELINE_REQUIRED_ACTION_FIELDS = {
  ADD_ALLOWANCE: ["access_template_id"],
  ADD_INVOICE_ITEM: ["name", "unit_amount"],
  ADD_TO_GROUP_CONVERSATION: ["target_group_conversation_id"],
  CHANGE_ACCOUNT_TYPE: ["type_label_id"],
  CHANGE_PRIMARY_LOCATION: ["location_id"],
  CHANGE_USER_STATUS: ["status"],
  CREATE_TASK: ["name"],
  POST_TO_FEED: ["message"],
  SEND_DOC_TO_SIGN: ["name"],
  SEND_EMAIL: ["body"],
  SEND_MESSAGE: ["message"],
  UPDATE_CUSTOM_PROPERTY: ["property_id", "value"],
};

/**
 * Mirrors api-v2 resolveRequiredConfigFields().
 *
 * @param {string} actionType
 * @param {{ triggerType?: string|null, recipeType?: string|null, isDefaultWorkflow?: boolean, includeOrgOptionFields?: boolean }} ctx
 * @returns {string[]}
 */
export function resolveRequiredActionFields(
  actionType,
  {
    triggerType = null,
    recipeType = null,
    isDefaultWorkflow = false,
    includeOrgOptionFields = true,
  } = {},
) {
  const trigger = triggerType || "NEW_ACTIVE_USER";
  let fields = [...(BASELINE_REQUIRED_ACTION_FIELDS[actionType] || [])];

  if (actionType === "SEND_EMAIL") {
    if (!BOOKING_TRIGGER_TYPES.has(trigger)) {
      fields.push("subject");
    }
    if (!isDefaultWorkflow && !recipeType) {
      fields.push("mail_provider_credentials_id");
    }
  }

  if (!includeOrgOptionFields) {
    fields = fields.filter((f) => !ORG_OPTION_REQUIRED_FIELDS.has(f));
  }

  return fields;
}

/**
 * Branch-required fields: required on the active config branch in the manual builder,
 * optional in api-v2. Mirror optix-ai BRANCH_REQUIRED_FIELDS. Each `when(config)` reads the
 * step's persisted action fields; SEND_DOC contents/contents_url stay out of here.
 */
export const BRANCH_REQUIRED_FIELDS = {
  SEND_EMAIL: [
    // Enrolled-member path (no specific admin): SendEmail.vue toEnrolledAccount.
    {
      field: "notification_type",
      when: (c) => !configValuePresent(c.target_admin_user_id),
    },
  ],
  CREATE_TASK: [
    // Not defaulting to the primary-location admin: CreateTask.vue assigneeMode ADMIN.
    {
      field: "assignee_user_id",
      when: (c) => c.set_assignee_primary_location_admin === false,
    },
  ],
  POST_TO_FEED: [
    // Not posting to all locations: PostToFeed.vue in_all_locations toggle.
    { field: "location_id", when: (c) => c.in_all_locations === false },
  ],
  ADD_ALLOWANCE: [
    {
      field: "allowance",
      when: (c) => isLimitedAllowanceConfig(c),
    },
    {
      field: "allowance_unit_id",
      when: (c) => isLimitedAllowanceConfig(c),
    },
  ],
};

export function isLimitedAllowanceConfig(config = {}) {
  if (config.unlimited_allowance === true) return false;
  const pct = config.allowance_percentage;
  if (typeof pct === "number" && pct > 0) return false;
  return true;
}

/**
 * @param {string} actionType
 * @param {object} config the step's nested action fields
 * @returns {string[]} branch-required fields whose branch is active for this config
 */
export function branchRequiredFields(actionType, config = {}) {
  return (BRANCH_REQUIRED_FIELDS[actionType] || [])
    .filter((entry) => entry.when(config || {}))
    .map((entry) => entry.field);
}

/**
 * True when the step was created by the AI in the current (pre-commit) session.
 * `ctx.aiSessionCreatedStepIds` may be a Set or array of stringified
 * `workflow_step_id`s (canvas `step_id` space).
 */
export function stepIsAiSessionCreated(step, ctx = {}) {
  const ids = ctx.aiSessionCreatedStepIds;
  const id = step?.workflow_step_id;
  if (!ids || id == null) return false;
  const key = String(id);
  return typeof ids.has === "function" ? ids.has(key) : ids.includes(key);
}

/**
 * @param {string} actionType
 * @param {{ triggerType?: string|null, recipeType?: string|null, isDefaultWorkflow?: boolean, resolvedFieldsByAction?: Record<string, string[]>, includeOrgOptionFields?: boolean }} ctx
 * @returns {string[]}
 */
export function requiredFieldsForAction(actionType, ctx = {}) {
  const map = ctx.resolvedFieldsByAction;
  if (map && Object.prototype.hasOwnProperty.call(map, actionType)) {
    return map[actionType];
  }
  return resolveRequiredActionFields(actionType, ctx);
}

/** Mirror api-v2 / optix-ai empty() semantics for required-field presence. */
export function configValuePresent(value) {
  if (value == null) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") return Boolean(value.trim());
  // Mirror optix-ai / api-v2 empty(): an empty array or object counts as missing.
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

export function actionNestedConfig(step, actionType) {
  const nestedKey = actionNestedFieldKey(actionType);
  return (nestedKey && step[nestedKey]) || {};
}

export function sendDocMissingDocumentSource(step) {
  if (step?.action_type !== "SEND_DOC_TO_SIGN") return false;
  const config = actionNestedConfig(step, "SEND_DOC_TO_SIGN");
  return (
    !configValuePresent(config.contents) &&
    !configValuePresent(config.contents_url)
  );
}

/**
 * @param {object} step
 * @param {{ triggerType?: string|null, recipeType?: string|null, isDefaultWorkflow?: boolean, includeOrgOptionFields?: boolean }} ctx
 * @returns {string[]}
 */
export function missingActionRequiredFields(step, ctx = {}) {
  const actionType = step?.action_type;
  if (!actionType) return ["action_type"];

  const nested = actionNestedConfig(step, actionType);
  const missing = requiredFieldsForAction(actionType, ctx).filter(
    (field) => !configValuePresent(nested[field]),
  );

  // Branch-required fields apply only to AI-session-created steps.
  if (stepIsAiSessionCreated(step, ctx)) {
    branchRequiredFields(actionType, nested).forEach((field) => {
      if (!configValuePresent(nested[field]) && !missing.includes(field)) {
        missing.push(field);
      }
    });
  }

  if (sendDocMissingDocumentSource(step) && !missing.includes("contents")) {
    missing.push("contents");
  }

  return missing;
}

export function triggerTypeFromSteps(steps) {
  for (const step of steps || []) {
    if (step?.trigger_type) return step.trigger_type;
  }
  return null;
}
